import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Layers, Search, X } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { SettingsCrumb } from '../admin/SettingsCrumb.js'
import { Link } from '../router.js'
import { Drawer, Select, adminMutate, apiGet, usePermissions, useToast } from '../admin/primitives.js'
import { ConfirmDialog } from '../auth/Dialog.js'
import { jourFr } from './lib.js'

/**
 * /settings/soirees/familles — FAMILLES D'ÉVÉNEMENTS (v4.1).
 *
 * Une famille = un concept de soirée réellement comparable — le carburant
 * des comparaisons et du forecast. Les SUGGESTIONS viennent de l'historique
 * réel (marques récurrentes des titres) ; l'admin confirme, fusionne ou
 * écarte. Bulk Action Bar pour confirmer en masse (jamais la Save Bar).
 */

interface Family {
  id: string; name: string; status: 'CONFIRMED' | 'SUGGESTED' | 'REJECTED'
  aliases: string[]; notes: string | null; events_count: number
}
interface Suggestion {
  brand: string; display: string; count: number
  first_date: string; last_date: string; samples: string[]
  avg_valid: number | null; avg_scanned: number | null; weekdays: string[]
}
interface FamilyDetail {
  family: Family
  events_count: number
  first_date: string | null; last_date: string | null
  weekdays: { day: string; count: number }[]
  median_scanned: number | null; mean_scanned: number | null
  best: { id: string; name: string; starts_at: string; scanned: number } | null
  worst: { id: string; name: string; starts_at: string; scanned: number } | null
  dispersion_mad: number | null
  events: { id: string; name: string; starts_at: string; scanned: number | null; valid: number | null
    confidence: string; source: string; atypical: boolean }[]
}

const CONF_BADGE: Record<string, string> = {
  CONFIRMED: 'confirmée', HIGH: 'auto · haute', MEDIUM: 'auto · moyenne', LOW: 'auto · basse', UNKNOWN: '—',
}

export function FamiliesPage() {
  const meta = useShellMeta()
  const toast = useToast()
  const permissions = usePermissions()
  const peutGerer = permissions.permissions.includes('soiree.manage')
  const [data, setData] = useState<{ families: Family[]; suggestions: Suggestion[]; unclassified_count: number } | null>(null)
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<FamilyDetail | null>(null)
  const [toReject, setToReject] = useState<Suggestion | null>(null)
  const [busy, setBusy] = useState(false)

  const charger = () => {
    void apiGet<NonNullable<typeof data>>('/api/v1/soirees/families').then(setData).catch(() => setData(null))
  }
  useEffect(charger, [])

  const needle = q.trim().toLowerCase()
  const families = useMemo(() => (data?.families ?? [])
    .filter((f) => f.status !== 'REJECTED')
    .filter((f) => needle === '' || f.name.toLowerCase().includes(needle))
    .sort((a, b) => b.events_count - a.events_count), [data, needle])
  const suggestions = useMemo(() => (data?.suggestions ?? [])
    .filter((s) => needle === '' || s.display.toLowerCase().includes(needle)), [data, needle])

  const confirmer = async (s: Suggestion) => {
    setBusy(true)
    const r = await adminMutate<{ ok?: boolean; error?: string }>('POST', '/api/v1/soirees/families', {
      name: s.display.toUpperCase(), aliases: [s.brand], status: 'CONFIRMED',
    })
    setBusy(false)
    if (r.status === 200) { toast('ok', `Famille « ${s.display} » confirmée.`); charger() }
    else toast('error', 'Confirmation impossible.')
  }

  const ecarter = async (s: Suggestion) => {
    const r = await adminMutate<{ ok?: boolean; error?: string }>('POST', '/api/v1/soirees/families', {
      name: s.display.toUpperCase(), aliases: [s.brand], status: 'REJECTED',
    })
    if (r.status === 200) { toast('ok', `« ${s.display} » ne sera plus proposé comme famille.`); charger() }
  }

  const bulkConfirm = async () => {
    const list = [
      ...suggestions.filter((s) => selected.has(s.brand))
        .map((s) => ({ name: s.display.toUpperCase(), aliases: [s.brand] })),
      ...families.filter((f) => f.status === 'SUGGESTED' && selected.has(f.name))
        .map((f) => ({ name: f.name, aliases: f.aliases })),
    ]
    if (list.length === 0) return
    setBusy(true)
    const r = await adminMutate<{ ok?: boolean; applied?: { classified: number }; error?: string }>(
      'POST', '/api/v1/soirees/families/bulk-confirm', { families: list })
    setBusy(false)
    if (r.status === 200) {
      toast('ok', `${list.length} familles confirmées — ${r.data?.applied?.classified ?? '?'} soirées classées.`)
      setSelected(new Set())
      charger()
    } else toast('error', 'Confirmation en masse impossible.')
  }

  const confirmFamily = async (f: Family) => {
    setBusy(true)
    const r = await adminMutate<{ ok?: boolean; error?: string }>('PATCH', `/api/v1/soirees/families/${f.id}`, { status: 'CONFIRMED' })
    setBusy(false)
    if (r.status === 200) { toast('ok', `Famille « ${f.name} » confirmée.`); charger() }
  }
  const rejectFamily = async (f: Family) => {
    setBusy(true)
    const r = await adminMutate<{ ok?: boolean; error?: string }>('PATCH', `/api/v1/soirees/families/${f.id}`, { status: 'REJECTED' })
    setBusy(false)
    if (r.status === 200) { toast('ok', `« ${f.name} » écartée — soirées reclassées.`); charger() }
  }

  const ouvrirDetail = (f: Family) => {
    void apiGet<FamilyDetail>(`/api/v1/soirees/families/${f.id}`).then(setDetail).catch(() => undefined)
  }

  const toggleSel = (brand: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(brand)) next.delete(brand); else next.add(brand)
      return next
    })
  }

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger">
        <SettingsCrumb />
        <div className="so-section-head">
          <div>
            <h1 className="lc-title"><Layers size={26} aria-hidden="true" /> Familles d'événements</h1>
            <p className="lc-subtitle">
              Le carburant des comparaisons et des prévisions — {data?.unclassified_count ?? '…'} soirée(s)
              sans famille (titres-dates et formats uniques inclus, c'est normal).
            </p>
          </div>
          <label className="so-search">
            <Search size={15} aria-hidden="true" />
            <input className="lc-input" type="search" placeholder="Rechercher" value={q}
              onChange={(e) => setQ(e.target.value)} aria-label="Rechercher une famille" />
          </label>
        </div>

        {/* ─── suggestions détectées ─── */}
        {suggestions.length > 0 ? (
          <section aria-labelledby="fam-suggestions">
            <h2 className="lc-section-title" id="fam-suggestions">Familles détectées dans l'historique</h2>
            <p className="lc-hint" style={{ marginTop: 0 }}>
              Marques récurrentes des titres (les DJs après « : » ne créent jamais de famille).
              Confirmez celles qui représentent un vrai concept de soirée.
            </p>
            <div className="so-sigs">
              {suggestions.map((s) => (
                <div key={s.brand} className={`so-sig fam-card ${selected.has(s.brand) ? 'fam-card--sel' : ''}`}>
                  <div className="fam-card__head">
                    {peutGerer ? (
                      <input type="checkbox" checked={selected.has(s.brand)} onChange={() => toggleSel(s.brand)}
                        aria-label={`Sélectionner ${s.display}`} />
                    ) : null}
                    <span className="so-sig__venue">{s.display}</span>
                    <span className="so-sig__badge">{s.count} soirées</span>
                  </div>
                  <span className="lc-hint">
                    {s.first_date} → {s.last_date} · {s.weekdays.join(' / ')}
                    {s.avg_scanned !== null ? ` · moy. ${s.avg_scanned} scans` : ''}
                  </span>
                  <span className="lc-hint fam-card__samples">{s.samples.slice(0, 2).join(' · ')}</span>
                  {peutGerer ? (
                    <span className="fam-card__actions">
                      <button type="button" className="lc-btn" disabled={busy} onClick={() => void confirmer(s)}>
                        <Check size={13} aria-hidden="true" /> Confirmer
                      </button>
                      <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setToReject(s)}>
                        Pas une famille
                      </button>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : data !== null ? (
          <p className="gd-calm">Aucune nouvelle famille récurrente détectée dans l'historique.</p>
        ) : null}

        {/* ─── familles enregistrées ─── */}
        <section aria-labelledby="fam-list" style={{ marginTop: 18 }}>
          <h2 className="lc-section-title" id="fam-list">Familles</h2>
          {families.length === 0 ? <p className="lc-hint">Aucune famille enregistrée.</p> : (
            <div className="so-sigs">
              {families.map((f) => (
                <div key={f.id} className={`so-sig fam-card ${selected.has(f.name) ? 'fam-card--sel' : ''}`}>
                  <div className="fam-card__head">
                    {peutGerer && f.status === 'SUGGESTED' ? (
                      <input type="checkbox" checked={selected.has(f.name)} onChange={() => toggleSel(f.name)}
                        aria-label={`Sélectionner ${f.name}`} />
                    ) : null}
                    <button type="button" className="so-clickable so-sig__venue" onClick={() => ouvrirDetail(f)}
                      aria-label={`Détail de la famille ${f.name}`}>{f.name}</button>
                    <span className="so-sig__badge">{f.events_count} soirées</span>
                  </div>
                  <span className="lc-hint">
                    {f.status === 'SUGGESTED' ? 'suggérée — à confirmer · ' : 'confirmée · '}
                    {f.aliases.length > 0 ? `${f.aliases.length} alias` : 'alias : nom seul'}
                  </span>
                  {peutGerer && f.status === 'SUGGESTED' ? (
                    <span className="fam-card__actions">
                      <button type="button" className="lc-btn" disabled={busy}
                        onClick={() => void confirmFamily(f)}>
                        <Check size={13} aria-hidden="true" /> Confirmer
                      </button>
                      <button type="button" className="lc-btn lc-btn--ghost" disabled={busy}
                        onClick={() => void rejectFamily(f)}>
                        Pas une famille
                      </button>
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ─── bulk action bar (≠ Save Bar) ─── */}
        {selected.size > 0 && peutGerer ? createPortal(
          <div className="lc-bulkbar-zone">
            <div className="lc-bulkbar" role="toolbar" aria-label="Actions sur la sélection">
              <span className="lc-bulkbar__count">
                <span className="lc-dot" /> {selected.size} suggestion{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''}
              </span>
              <span className="lc-bulkbar__actions">
                <button className="lc-btn lc-btn--primary" disabled={busy} onClick={() => void bulkConfirm()}>
                  <Check size={14} aria-hidden="true" /> Confirmer la sélection
                </button>
              </span>
              <button className="lc-btn lc-btn--ghost lc-bulkbar__clear" onClick={() => setSelected(new Set())}
                aria-label="Vider la sélection"><X size={15} /></button>
            </div>
          </div>, document.body,
        ) : null}

        <ConfirmDialog
          open={toReject !== null}
          title="Ne pas considérer comme famille ?"
          confirmLabel="Écarter"
          onConfirm={() => { if (toReject) void ecarter(toReject); setToReject(null) }}
          onClose={() => setToReject(null)}
        >
          <p>« {toReject?.display} » ne sera plus proposé. Les soirées concernées resteront
            sans famille (comparées par jour de semaine). Réversible dans l'audit.</p>
        </ConfirmDialog>

        <FamilyDrilldown detail={detail} onClose={() => setDetail(null)} canManage={peutGerer}
          allFamilies={(data?.families ?? []).filter((f) => f.status === 'CONFIRMED')}
          onChanged={() => { charger(); if (detail) void apiGet<FamilyDetail>(`/api/v1/soirees/families/${detail.family.id}`).then(setDetail).catch(() => setDetail(null)) }} />
      </main>
    </Shell>
  )
}

/* ═══════════ drilldown (§62) ═══════════ */

function FamilyDrilldown({ detail, onClose, canManage, allFamilies, onChanged }: {
  detail: FamilyDetail | null
  onClose: () => void
  canManage: boolean
  allFamilies: Family[]
  onChanged: () => void
}) {
  const toast = useToast()
  const [mergeInto, setMergeInto] = useState('')
  const [confirmMerge, setConfirmMerge] = useState(false)
  useEffect(() => { setMergeInto('') }, [detail?.family.id])
  if (!detail) return null
  const f = detail.family

  const overrideEvent = async (eventId: string, family: string | null) => {
    const r = await adminMutate<{ ok?: boolean; error?: string }>('PATCH', `/api/v1/soirees/${eventId}/family`, { family })
    if (r.status === 200) { toast('ok', 'Famille corrigée (traçée dans l’audit).'); onChanged() }
  }

  const doMerge = async () => {
    const r = await adminMutate<{ ok?: boolean; moved?: number; error?: string }>(
      'POST', `/api/v1/soirees/families/${f.id}/merge`, { into_id: mergeInto })
    if (r.status === 200) { toast('ok', `Fusion faite — ${r.data?.moved ?? '?'} soirée(s) réaffectée(s).`); onClose(); onChanged() }
    else toast('error', 'Fusion impossible.')
  }

  return (
    <Drawer open title={f.name} onClose={onClose}>
      <dl className="lc-kv">
        <div><dt>Soirées</dt><dd>{detail.events_count} · {detail.first_date} → {detail.last_date}</dd></div>
        <div><dt>Jours</dt><dd>{detail.weekdays.slice(0, 3).map((w) => `${w.day} (${w.count})`).join(' · ') || '—'}</dd></div>
        <div><dt>Scans</dt><dd>
          {detail.median_scanned !== null
            ? `médiane ${detail.median_scanned} · moyenne ${detail.mean_scanned} · dispersion ±${detail.dispersion_mad}`
            : 'aucune donnée de scans'}
        </dd></div>
        {detail.best ? <div><dt>Record</dt><dd><Link href={`/soirees/${detail.best.id}`}>{detail.best.name}</Link> · {detail.best.scanned} scans</dd></div> : null}
        {detail.worst && detail.worst.id !== detail.best?.id
          ? <div><dt>Plus faible</dt><dd><Link href={`/soirees/${detail.worst.id}`}>{detail.worst.name}</Link> · {detail.worst.scanned} scans</dd></div> : null}
        <div><dt>Alias</dt><dd>{[f.name, ...f.aliases].filter((v, i, a) => a.indexOf(v) === i).join(' · ')}</dd></div>
        <div><dt>Statut</dt><dd>{f.status === 'CONFIRMED' ? 'confirmée' : 'suggérée'}</dd></div>
      </dl>

      {canManage && allFamilies.filter((x) => x.id !== f.id).length > 0 ? (
        <div className="so-factors">
          <h3 className="so-factors__title">Fusionner dans une autre famille</h3>
          <span style={{ display: 'flex', gap: 8 }}>
            <Select value={mergeInto} ariaLabel="Famille cible de la fusion"
              options={[{ value: '', label: '— choisir —' },
                ...allFamilies.filter((x) => x.id !== f.id).map((x) => ({ value: x.id, label: x.name }))]}
              onChange={setMergeInto} />
            <button type="button" className="lc-btn" disabled={!mergeInto} onClick={() => setConfirmMerge(true)}>Fusionner</button>
          </span>
          <p className="lc-hint">Non destructif : les soirées gardent leur identité, seuls la famille et les alias bougent.</p>
        </div>
      ) : null}

      <div className="so-factors">
        <h3 className="so-factors__title">Soirées de la famille</h3>
        <div className="fam-events">
          {detail.events.map((e) => (
            <div key={e.id} className="fam-event">
              <span className="fam-event__body">
                <Link href={`/soirees/${e.id}`}>{e.name}</Link>
                <span className="lc-hint">
                  {jourFr(e.starts_at)}{e.scanned !== null ? ` · ${e.scanned} scans` : ''}
                  {' · affectation '}{CONF_BADGE[e.confidence] ?? e.confidence}
                  {e.source === 'MANUAL' ? ' (manuelle)' : ''}
                </span>
              </span>
              {e.atypical ? <span className="fam-atypique" title="Très éloignée du médian de la famille — pondérée à la baisse dans les prévisions, jamais supprimée.">ATYPIQUE</span> : null}
              {canManage ? (
                <Select compact value={f.name} ariaLabel={`Corriger la famille de ${e.name}`}
                  options={[
                    ...allFamilies.map((x) => ({ value: x.name, label: x.name })),
                    { value: '__none', label: 'Aucune famille' },
                  ]}
                  onChange={(v) => void overrideEvent(e.id, v === '__none' ? null : v)} />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      <ConfirmDialog
        open={confirmMerge}
        title={`Fusionner « ${f.name} » ?`}
        confirmLabel="Fusionner"
        onConfirm={() => { setConfirmMerge(false); void doMerge() }}
        onClose={() => setConfirmMerge(false)}
      >
        <p>Les {detail.events_count} soirée(s) et les alias de « {f.name} » rejoindront
          « {allFamilies.find((x) => x.id === mergeInto)?.name ?? '' } ». Audité, IDs inchangés.</p>
      </ConfirmDialog>
    </Drawer>
  )
}
